"use client";

import { useState, useRef, FormEvent, useEffect } from "react";
import { createPortal } from "react-dom";
import { usePathname } from "next/navigation";
import { captureLeadCaptured } from "@/library/analytics";
import { submitLeadIntake } from "@/library/leads";
import { clientEventIdFor, releaseClientEventId } from "@/library/ad-touch";
import { fireLeadEvent } from "@/library/ad-tags";
import BookAuditCallButton from "@/app/components/BookAuditCallButton";

// ============ TOAST COMPONENT ============
interface ToastProps {
  show: boolean;
  message: string;
  position?: "top" | "bottom";
  toastType?: "success" | "error";
}

function Toast({
  show,
  message,
  position = "top",
  toastType = "success",
}: ToastProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const toastContent = (
    <div
      className={`fixed ${position === "top" ? "top-6" : "bottom-6"} right-6 text-on-accent px-6 py-4 rounded-2xl shadow-2xl transform transition-all duration-500 z-[9999] font-semibold text-sm flex items-center gap-3 ${show ? "translate-x-0 opacity-100 scale-100" : "translate-x-full opacity-0 scale-90"} ${toastType === "success" ? "bg-accent" : "bg-signal-negative"}`}
    >
      <span className="flex items-center justify-center w-6 h-6 bg-white/20 rounded-full text-xs">
        {toastType === "success" ? "✓" : "✗"}
      </span>
      {message}
    </div>
  );

  if (!mounted) return null;

  return createPortal(toastContent, document.body);
}


// ============ LEAD FORM COMPONENT ============
function LeadForm() {
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const pathname = usePathname();

  const GOOGLE_SCRIPT_URL = process.env.NEXT_PUBLIC_GOOGLE_SCRIPT_URL;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const data = new FormData(e.currentTarget);
    const formValues = Object.fromEntries(data.entries());

    try {
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      });

      // Second write, to the Revenue API, so these leads also reach the CRM.
      // The Apps Script POST above is unchanged and still owns the team's
      // sheet, which is the only place the qualifying answers (website,
      // industry, company size, region) are kept — the intake schema has no
      // fields for them and refuses unknown ones.
      //
      // Deliberately NOT blocking on a refusal. This form has always accepted
      // any address, and the intake turns away free-mail ones; letting that
      // 422 stop the submission would quietly start rejecting free-report
      // signups the sheet takes today. A refused lead lands in the sheet and
      // not in the CRM — exactly what happens today for every lead here.
      //
      // NOTE: no `honeypot` is passed. This form's own `website` input is the
      // prospect's real, required URL, not a decoy, and sending it as the
      // honeypot would make the intake discard every lead with a 202. See
      // `library/leads.ts`.
      const clientEventId = clientEventIdFor("lead-generation-hero");
      const intake = await submitLeadIntake({
        clientEventId,
        email: String(formValues.email ?? ""),
        source: "website_lead_form",
        name: [formValues.firstName, formValues.lastName]
          .filter(Boolean)
          .join(" "),
        phone: String(formValues.contact ?? ""),
        message: String(formValues.comments ?? ""),
      });

      // The Apps Script call is `mode: "no-cors"`, so its response is opaque
      // and the strongest thing knowable about it is that it did not throw.
      // The intake's 202 is real evidence, so the event is fired on that
      // instead: `lead_captured` now means the lead was stored, not that a
      // request left the browser.
      if (intake.status === "stored") {
        releaseClientEventId("lead-generation-hero");
        fireLeadEvent(clientEventId);
        captureLeadCaptured({
          source: "lead-generation-hero",
          email: String(formValues.email ?? ""),
          pathname,
          properties: {
            industry: formValues.business || null,
            company_size: formValues.companySize || null,
            region: formValues.region || null,
          },
        });
      }

      setToastType("success");
      setShowToast(true);
      setToastMsg("Thank you! Your free AI Visibility Report is on its way.");
      setTimeout(() => {
        setIsSubmitting(false);
        setShowToast(false);
      }, 3000);
      formRef.current?.reset();
    } catch (error) {
      console.error("Error:", error);
      setToastType("error");
      setShowToast(true);
      setToastMsg("Failed to submit. Please try again.");
      setTimeout(() => {
        setIsSubmitting(false);
        setShowToast(false);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div id="contact">
        <div className="bg-surface-raised border border-hairline rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-lg hover:shadow-xl transition-all duration-300 relative group">
          <div className="flex items-center gap-2 sm:gap-3 mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-accent rounded-lg text-on-accent flex-shrink-0">
              <svg
                className="w-4 h-4 sm:w-5 sm:h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
            </div>
            {/* h2 (not h3): this renders in the second grid column, immediately
                after the page's <h1> in DOM order — h3 here would skip a level. */}
            <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-ink">
              Get your free AI Visibility Report
            </h2>
          </div>
          <p className="text-ink-muted text-xs sm:text-sm mb-3 sm:mb-4">
            See how your brand ranks on ChatGPT, Claude & Gemini vs your
            competitors. Takes 60 seconds.
          </p>

          <form
            ref={formRef}
            className="flex flex-col gap-3"
            onSubmit={handleSubmit}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-3">
                <label className="text-xs font-semibold text-ink-muted">
                  First name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  placeholder="First name"
                  required
                  className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 placeholder:text-ink-faint"
                />
              </div>
              <div className="flex flex-col gap-3">
                <label className="text-xs font-semibold text-ink-muted">
                  Last name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  placeholder="Last name"
                  required
                  className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 placeholder:text-ink-faint"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-3">
                <label className="text-xs font-semibold text-ink-muted">
                  Work email address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  required
                  className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 placeholder:text-ink-faint"
                />
              </div>
              <div className="flex flex-col gap-3">
                <label className="text-xs font-semibold text-ink-muted">
                  Contact number
                </label>
                <input
                  type="tel"
                  name="contact"
                  placeholder="Contact number"
                  className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 placeholder:text-ink-faint"
                />
              </div>
            </div>
            <div className="gap-3">
              <div className="flex flex-col gap-3">
                <label className="text-xs font-semibold text-ink-muted">
                  Your website URL <span className="text-red-500">*</span>
                </label>
                <input
                  type="url"
                  name="website"
                  placeholder="e.g. yourcompany.com"
                  required
                  className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 placeholder:text-ink-faint"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex flex-col gap-3">
                <label className="text-xs font-semibold text-ink-muted">
                  Industry
                </label>
                <select
                  name="business"
                  className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15"
                >
                  <option value="">Select industry</option>
                  <option>Technology / SaaS</option>
                  <option>Financial Services</option>
                  <option>Healthcare</option>
                  <option>E-commerce / Retail</option>
                  <option>Manufacturing</option>
                  <option>Professional Services</option>
                  <option>Marketing / Agency</option>
                  <option>Other</option>
                </select>
              </div>
              <div className="flex flex-col gap-3">
                <label className="text-xs font-semibold text-ink-muted">
                  Company size
                </label>
                <select
                  name="companySize"
                  className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15"
                >
                  <option value="">Select size</option>
                  <option>1-10 employees</option>
                  <option>11-50 employees</option>
                  <option>51-200 employees</option>
                  <option>201-500 employees</option>
                  <option>500+ employees</option>
                </select>
              </div>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-xs font-semibold text-ink-muted">
                Target Region
              </label>
              <select
                name="region"
                className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15"
              >
                <option value="">Select a country...</option>
                <option>United States</option>
                <option>United Kingdom</option>
                <option>Canada</option>
                <option>Australia</option>
                <option>Germany</option>
                <option>France</option>
                <option>India</option>
                <option>Singapore</option>
                <option>United Arab Emirates</option>
                <option>Netherlands</option>
                <option>Japan</option>
                <option>South Korea</option>
                <option>Brazil</option>
                <option>Mexico</option>
                <option>Other</option>
              </select>
            </div>

            <div className="flex flex-col gap-3">
              <label className="text-xs font-semibold text-ink-muted">
                Comments
              </label>
              <textarea
                name="comments"
                placeholder="How can we help?"
                rows={3}
                className="w-full px-3 py-2.5 rounded-md bg-surface border-2 border-transparent text-ink text-sm transition-all duration-300 hover:bg-surface focus:outline-none focus:border-accent focus:ring-4 focus:ring-accent/15 placeholder:text-ink-faint resize-none"
              />
            </div>

            <button
              className="w-full mt-1 px-6 py-3 rounded-lg bg-accent border border-accent text-on-accent font-bold text-sm md:text-base flex items-center justify-center gap-2 transition-all duration-300 hover:opacity-90 active:scale-[0.97] disabled:opacity-70 disabled:cursor-not-allowed group relative overflow-hidden cursor-pointer"
              type="submit"
              disabled={isSubmitting}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></span>
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin w-5 h-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                  </svg>
                  Submitting...
                </>
              ) : (
                <>
                  Get My Free AI Report
                  <svg
                    className="w-4 h-4 transition-transform group-hover:translate-x-1"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                    <polyline points="12 5 19 12 12 19"></polyline>
                  </svg>
                </>
              )}
            </button>
          </form>

          {/* Trust Section with Bullet Points */}
          <div className="mt-4 pt-4 border-t border-hairline">
            <p className="text-xs font-semibold text-ink mb-2">
              What&apos;s included in your free report:
            </p>
            <ul className="space-y-1.5 mb-3">
              <li className="flex items-center gap-2 text-xs text-ink-muted">
                <svg
                  className="w-3.5 h-3.5 text-accent flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Your AI Visibility Score across the AI engines your buyers use
              </li>
              <li className="flex items-center gap-2 text-xs text-ink-muted">
                <svg
                  className="w-3.5 h-3.5 text-accent flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Competitor comparison & ranking
              </li>
              <li className="flex items-center gap-2 text-xs text-ink-muted">
                <svg
                  className="w-3.5 h-3.5 text-accent flex-shrink-0"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                Top 3 recommendations to improve
              </li>
            </ul>
            {/* Trust indicators */}
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[10px] text-ink-faint">
              <span>Instant access</span>
              <span className="text-hairline">•</span>
              <span>No spam</span>
              <span className="text-hairline">•</span>
              <span>60 seconds</span>
            </div>
          </div>
        </div>
      </div>
      <Toast show={showToast} message={toastMsg} toastType={toastType} />
    </>
  );
}

// ============ HERO COMPONENT ============
export default function Hero() {
  return (
    <section
      id="hero-section"
      className="relative py-20 md:py-24 lg:py-28 flex items-center overflow-hidden bg-surface"
    >
      {/* Glow Background */}
      <div className="absolute -top-1/5 left-[10%] w-[800px] h-[800px] bg-[radial-gradient(circle,rgba(28,164,147,0.08)_0%,transparent_60%)] blur-[100px] -z-10 animate-pulse-glow"></div>

      {/* Floating Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none -z-10 hidden md:block">
      </div>

      <div className="max-w-[1200px] mx-auto px-4 md:px-8 lg:px-12 grid grid-cols-1 md:grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 md:gap-10 lg:gap-16 items-center">
        <div className="text-center lg:text-left">
          {/* Eyebrow Tag */}
          <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-accent/10 border border-accent/30 rounded-full text-accent text-xs sm:text-sm font-semibold mb-4 sm:mb-6 animate-fade-in-up">
            <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-accent rounded-full animate-pulse flex-shrink-0"></span>
            <span>Done-for-you AI-search lead generation</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold leading-[1.15] tracking-tight mb-4 sm:mb-6 text-ink animate-fade-in-up animation-delay-100">
            Get qualified inbound leads from AI search.
          </h1>

          {/* Punch Line - Improved Design */}
          <div className="mb-4 sm:mb-6 animate-fade-in-up animation-delay-200 bg-surface-raised border border-hairline rounded-lg sm:rounded-xl p-3 sm:p-4 relative overflow-hidden">
            <p className="text-sm sm:text-base md:text-lg text-ink font-semibold">
              You&apos;re not losing deals to better products.
            </p>
            <p className="text-sm sm:text-base md:text-lg font-bold text-accent">
              You&apos;re losing them to brands AI mentions first.
            </p>
          </div>

          {/* Key Differentiator Callout */}
          <div className="bg-surface-raised border border-hairline p-3 sm:p-4 mb-4 sm:mb-6 text-left animate-fade-in-up animation-delay-300">
            <p className="text-[10px] sm:text-xs text-ink-faint uppercase tracking-wider font-semibold mb-0.5 sm:mb-1">
              The Key Difference
            </p>
            <p className="text-base sm:text-lg font-bold text-ink mb-0.5 sm:mb-1">
              Google captures intent. AI creates it.
            </p>
            <p className="text-xs sm:text-sm text-ink-muted">
              GEO & AEO puts your brand inside the answer at the moment a
              buyer&apos;s idea is being formed, not after they&apos;ve already
              decided what to search for.
            </p>
          </div>

          {/* Primary CTA — book a call; the form (right) is the secondary free-report hook */}
          <div className="mb-4 sm:mb-6 animate-fade-in-up animation-delay-300">
            <BookAuditCallButton
              label="Book an Audit Call"
              className="inline-flex items-center justify-center w-full sm:w-auto px-6 py-3.5 bg-accent text-on-accent font-semibold text-[15px] rounded-lg hover:opacity-90 active:scale-[0.97] transition-all cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            />
            <p className="text-[12px] text-ink-faint mt-2">Or get a free AI Visibility report → fill the form.</p>
          </div>

          {/* Supporting Statistics */}
          <div className="flex flex-wrap justify-center lg:justify-start gap-4 sm:gap-6 pt-4 sm:pt-6 border-t border-hairline animate-fade-in-up animation-delay-400">
            <div className="flex flex-col gap-0.5 sm:gap-1 text-center lg:text-left min-w-[90px] sm:min-w-[100px]">
              <span className="text-xl sm:text-2xl md:text-3xl font-extrabold text-accent">
                9
              </span>
              <span className="text-[10px] sm:text-xs text-ink-muted max-w-[100px] sm:max-w-[120px]">
                supported AI engines, selected around your buyers
              </span>
            </div>
            <div className="flex flex-col gap-0.5 sm:gap-1 text-center lg:text-left min-w-[90px] sm:min-w-[100px]">
              <span className="text-xl sm:text-2xl md:text-3xl font-extrabold text-accent">
                4
              </span>
              <span className="text-[10px] sm:text-xs text-ink-muted max-w-[100px] sm:max-w-[120px]">
                managed stages from visibility to enquiry reporting
              </span>
            </div>
            <div className="flex flex-col gap-0.5 sm:gap-1 text-center lg:text-left min-w-[90px] sm:min-w-[100px]">
              <span className="text-xl sm:text-2xl md:text-3xl font-extrabold text-accent">
                1
              </span>
              <span className="text-[10px] sm:text-xs text-ink-muted max-w-[100px] sm:max-w-[120px]">
                on-domain journey from answer-ready page to enquiry
              </span>
            </div>
          </div>

        </div>

        <div className="animate-fade-in-up animate-scale-in animation-delay-200">
          <LeadForm />
        </div>
      </div>
    </section>
  );
}
